"use client";
import { Nft } from "@/api/nfts/types";
import {
  OfferOrder,
  OrderType,
  SignedSaleOrder,
  TimedAuctions,
} from "@/api/orders/types";
import { Button } from "@/app/(components)/Basic/Button";
import { Type } from "@/app/(components)/Basic/Button/constants";
import { CryptoValue } from "@/app/(components)/Basic/CryptoValue";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import OfferIcon from "@/assets/icons/offer.svg";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { CancelOfferButton } from "../Buttons/CancelOffer";
import { MakeOfferButton } from "../Buttons/MakeOffer";
import { TradeActions } from "../TradeActions";
import { getHighestOffer, getYourOffer } from "./helpers";
import useMe from "@/shared/hooks/useMe";
import Link from "next/link";
import useCurrentPrice from "./hooks/useCurrentPrice";
import useTradeLabel from "./hooks/useTradeLabel";
import { isExpiredInSeconds } from "@/shared/dates";
import { isUserPrivateAuctionParticipant } from "../TradeActions/helpers";

export interface Props {
  nft: Nft;
  auction: SignedSaleOrder;
  offers: OfferOrder[];
}

export default function TradeDetails({ nft, auction, offers }: Props) {
  const { me } = useMe();

  const highestOffer = useMemo(
    () => getHighestOffer(offers, me?.address, nft.owner.address!),
    [me?.address, nft.owner.address, offers],
  );

  const yourOffer = useMemo(
    () => getYourOffer(offers, me?.address),
    [me?.address, offers],
  );

  const tradeLabel = useTradeLabel(auction);
  const currentPrice = useCurrentPrice(auction);

  const isPrivateAuctionHidden = useMemo(
    () =>
      !!auction &&
      auction.type === OrderType.PRIVATE_AUCTION &&
      (!isUserPrivateAuctionParticipant(me!, auction) ||
        isExpiredInSeconds(auction.expiresAt)),
    [auction, isUserPrivateAuctionParticipant, me],
  );

  return (
    <>
      <div
        className={twMerge(
          "border border-dotted rounded-2xl p-15 lg:flex justify-between  w-full text-center mb-10 lg:mb-20 border-b",
          !auction || !TimedAuctions.includes(auction.type!) ? "-mt-20" : "",
        )}
      >
        <div className="w-full flex flex-col items-center lg:border-r lg:border-dotted after:content-[''] after:block after:w-20 after:h-[1px] after:border-b after:border-dotted after:mt-10 lg:after:hidden py-6 relative lg:min-h-[130px]">
          <div className="text-secondary text-body-2 mb-1 endemic:text-primary">
            {tradeLabel}
          </div>
          <CryptoValue
            cryptoValue={isPrivateAuctionHidden ? undefined : currentPrice}
            paymentErc20TokenAddress={
              isPrivateAuctionHidden
                ? undefined
                : auction?.paymentErc20TokenAddress
            }
            isLightEther
          />
          <TradeActions nft={nft} auction={auction} disable={!me} />
        </div>
        <div className="w-full flex flex-col items-center lg:border-r lg:border-dotted after:content-[''] after:block after:w-20 after:h-[1px] after:border-b after:border-dotted after:mt-10 lg:after:hidden py-6 relative lg:min-h-[130px]">
          <div className="text-secondary text-body-2 mb-1 endemic:text-primary">
            Best offer
          </div>
          <CryptoValue
            cryptoValue={highestOffer?.price}
            paymentErc20TokenAddress={highestOffer?.paymentErc20TokenAddress}
            isLightEther
          />
          <Button
            element={!highestOffer ? Type.Button : Type.Link}
            disabled={!highestOffer}
            className="-bottom-20 mt-10 lg:mt-0 lg:absolute"
            startIcon={<OfferIcon />}
            endIcon={<ArrowRightIcon className="w-4" />}
            href={`/nft/${nft.id}/offers`}
          >
            View offers
          </Button>
        </div>
        <div className="w-full flex flex-col items-center relative py-6 lg:min-h-[130px]">
          <div className="text-secondary text-body-2 mb-1 endemic:text-primary">
            Your offer
          </div>
          <CryptoValue
            cryptoValue={yourOffer?.price}
            paymentErc20TokenAddress={yourOffer?.paymentErc20TokenAddress}
            hideIcon
            isLightEther
          />
          {yourOffer ? (
            <CancelOfferButton
              offer={{ ...yourOffer, nft }}
              className="-bottom-20 mt-10 lg:mt-0 lg:absolute"
              disable={!me}
            />
          ) : (
            <MakeOfferButton nft={nft} disable={!me} />
          )}
        </div>
      </div>
      {!me && (
        <Link
          href="/login"
          className="block text-body-2 text-green underline w-full text-center"
        >
          Login to make offer or buy NFT
        </Link>
      )}
    </>
  );
}
